/**
 * Hero slideshow — smooth the loop back to the first slide.
 *
 * The problem: assets/slider.js animates a move between adjacent slides, but a
 * wrap is not adjacent. With a single visible slide it takes the instant path
 * (slider.js:353-356), so slide 1 -> 2 glides one slide width while the last
 * slide -> first is a hard cut. On a 4-slide hero that cut has to cover 3 slide
 * widths, which is why animating it instead would read as a fast rewind.
 *
 * The approach: leave the reposition instant — no backwards motion — and mask it
 * with a short fade, so it reads as a deliberate transition.
 *
 * Why a MutationObserver: its callback runs as a microtask, after slider.js has
 * moved the scroller but BEFORE the browser paints. That lets the fade start at
 * opacity 0 on the very frame of the jump, so there is no flash of the new slide
 * at full opacity first. A scroll or timer-based hook would land a frame late.
 *
 * Scoped to `.hero-slideshow`, so every other carousel in the theme keeps its
 * existing behaviour. Does nothing when the fade duration is 0 or the visitor
 * prefers reduced motion.
 */

const WRAPPING_CLASS = 'is-wrapping';
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

/** Index of the slide slider.js currently considers visible. */
function currentIndex(slides) {
  return slides.findIndex((slide) => slide.getAttribute('aria-hidden') === 'false');
}

function setup(carousel) {
  if (carousel.dataset.loopFadeBound) return;
  carousel.dataset.loopFadeBound = 'true';

  const scroller = carousel.querySelector('carousel-slides');
  if (!scroller) return;

  const slides = [...carousel.querySelectorAll('carousel-slide')];
  // A wrap only exists with something to wrap around.
  if (slides.length < 3) return;

  const last = slides.length - 1;
  let previous = currentIndex(slides);
  let clearTimer = null;

  const observer = new MutationObserver(() => {
    const next = currentIndex(slides);
    if (next === -1 || next === previous) return;

    const wrappedForward = previous === last && next === 0;
    const wrappedBackward = previous === 0 && next === last;
    previous = next;

    if (!wrappedForward && !wrappedBackward) return;
    if (REDUCED_MOTION.matches) return;

    const duration = Number.parseInt(
      getComputedStyle(carousel).getPropertyValue('--hero-loop-fade'),
      10
    );
    if (!duration) return;

    // Restart cleanly if a wrap happens while one is still fading.
    scroller.classList.remove(WRAPPING_CLASS);
    // Force a style recalculation so re-adding the class replays the animation.
    void scroller.offsetWidth;
    scroller.classList.add(WRAPPING_CLASS);

    window.clearTimeout(clearTimer);
    clearTimer = window.setTimeout(() => {
      scroller.classList.remove(WRAPPING_CLASS);
    }, duration + 60);
  });

  for (const slide of slides) {
    observer.observe(slide, { attributes: true, attributeFilter: ['aria-hidden'] });
  }
}

function scan(root = document) {
  for (const carousel of root.querySelectorAll('.hero-slideshow slide-carousel')) {
    setup(carousel);
  }
}

scan();

// Re-bind when the theme editor swaps the section in.
document.addEventListener('shopify:section:load', (event) => scan(event.target));
